<template>
  <AlascoUnderlineTabs
    v-model="activeTab"
    :tabs="visibleTabs"
    :default-value="defaultTab"
    class="tw-h-full"
    content-class="tw-mt-5 tw-space-y-8"
  >
    <template #details>
      <slot name="details" />
    </template>
    <template v-if="showComments" #comments>
      <slot name="comments" />
    </template>
    <template v-if="showNotes" #notes>
      <slot name="notes" />
    </template>
    <template v-if="showRelated" #related>
      <slot name="related" />
    </template>
  </AlascoUnderlineTabs>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { AlascoUnderlineTabs, type AlascoUnderlineTab } from '@/components/shared/tabs';

interface Props {
  detailsLabel?: string;
  showComments?: boolean;
  showNotes?: boolean;
  showRelated?: boolean;
  defaultTab?: string;
  /** Show an unread dot on the Comments tab. */
  commentsUnread?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showComments: true,
  showNotes: true,
  showRelated: true,
  defaultTab: 'details',
});

const emit = defineEmits<{ 'seen-comments': [] }>();

const { t } = useI18n();
const activeTab = ref(props.defaultTab);

// Opening the Comments tab counts as reading them.
watch(activeTab, value => {
  if (value === 'comments') emit('seen-comments');
});

const visibleTabs = computed<AlascoUnderlineTab[]>(() => {
  const tabs: AlascoUnderlineTab[] = [
    {
      value: 'details',
      label: props.detailsLabel || t('orders.titles.order_details'),
    },
  ];

  if (props.showComments) {
    tabs.push({
      value: 'comments',
      label: t('orders.titles.comments'),
      dot: props.commentsUnread,
    });
  }

  if (props.showNotes) {
    tabs.push({ value: 'notes', label: t('orders.titles.self_notes') });
  }

  if (props.showRelated) {
    tabs.push({ value: 'related', label: t('orders.documents.related') });
  }

  return tabs;
});
</script>
